import { useState } from 'react';
import { User, Phone, MapPin, Briefcase, GraduationCap, Calendar, CreditCard, AlertCircle } from 'lucide-react';
import { FormData } from '../App';
import { interviewService } from '../services/interview.service';

interface Step1Props {
  formData: FormData;
  onUpdate: (data: Partial<FormData>) => void;
  onNext: () => void;
  onSituationCreated: (situationId: number) => void;
  userId: number;
}

export function Step1StudentInfo({ formData, onUpdate, onNext, onSituationCreated, userId: _userId }: Step1Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await interviewService.updateUserData(formData);
      onSituationCreated(response.situation_id);
      onNext();
    } catch (err: any) {
      console.error('Error updating user data:', err);
      setError(err.message || 'خطا در ذخیره اطلاعات. لطفاً دوباره تلاش کنید.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    onUpdate({ [field]: value });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Alert Box */}
      <div
        className="rounded-2xl p-6 border-2 flex items-start gap-4"
        style={{
          backgroundColor: '#FEF9EC',
          borderColor: 'var(--primary-gold)',
        }}
      >
        <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: 'var(--secondary-green)' }} />
        <div>
          <p className="leading-relaxed" style={{ color: 'var(--secondary-green)' }}>
            <strong>توجه:</strong> این فرم برای پیش‌ثبت‌نام <strong>کودکستان</strong> و{' '}
            <strong>کلاس اول</strong> است.
            <br />
            پس از تکمیل این فرم، باید برای زمان مصاحبه اقدام کنید.
          </p>
        </div>
      </div>

      {/* Student Information Section */}
      <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b-2" style={{ borderColor: 'var(--border-light)' }}>
          <div className="p-2 rounded-lg" style={{ backgroundColor: '#F0F9F8' }}>
            <User className="w-6 h-6" style={{ color: 'var(--secondary-green)' }} />
          </div>
          <h2 className="text-2xl" style={{ color: 'var(--secondary-green)' }}>
            اطلاعات دانش‌آموز
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="form-group">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              نام *
            </label>
            <input
              type="text"
              required
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2"
              style={{
                borderColor: 'var(--border-light)',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--primary-gold)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-light)';
              }}
            />
          </div>

          <div className="form-group">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              نام خانوادگی *
            </label>
            <input
              type="text"
              required
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2"
              style={{
                borderColor: 'var(--border-light)',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--primary-gold)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-light)';
              }}
            />
          </div>

          <div className="form-group">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              پایه تحصیلی مورد نظر *
            </label>
            <select
              required
              value={formData.desiredGrade}
              onChange={(e) => handleChange('desiredGrade', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2"
              style={{
                borderColor: 'var(--border-light)',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--primary-gold)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-light)';
              }}
            >
              <option value="">انتخاب پایه</option>
              <option value="pre-school">کودکستان</option>
              {/* <option value="first-grade">کلاس اول</option> */}
            </select>
          </div>

          <div className="form-group">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              کد ملی *
            </label>
            <div className="relative">
              <CreditCard
                className="absolute left-auto right-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: 'var(--text-gray)' }}
              />
              <input
                type="text"
                required
                value={formData.nationalId}
                onChange={(e) => {
                  // Only allow digits, max 10 digits
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  handleChange('nationalId', value);
                }}
                pattern="[0-9]{10}"
                maxLength={10}
                className="w-full pr-11 pl-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--primary-gold)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-light)';
                  // Validate exactly 10 digits
                  if (e.target.value.length !== 10) {
                    e.target.setCustomValidity('کد ملی باید دقیقاً ۱۰ رقم باشد');
                  } else {
                    e.target.setCustomValidity('');
                  }
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              تاریخ تولد *
            </label>
            <div className="relative">
              <Calendar
                className="absolute left-auto right-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: 'var(--text-gray)' }}
              />
              <input
                type="text"
                required
                value={formData.dateOfBirth}
                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                placeholder="مثال: ۱۳۸۵/۰۵/۱۵"
                className="w-full pr-11 pl-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--primary-gold)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-light)';
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              محل تولد *
            </label>
            <div className="relative">
              <MapPin
                className="absolute left-auto right-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: 'var(--text-gray)' }}
              />
              <input
                type="text"
                required
                value={formData.placeOfBirth}
                onChange={(e) => handleChange('placeOfBirth', e.target.value)}
                className="w-full pr-11 pl-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--primary-gold)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-light)';
                }}
              />
            </div>
          </div>

          <div className="form-group sm:col-span-2">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              آدرس منزل *
            </label>
            <textarea
              required
              value={formData.homeAddress}
              onChange={(e) => handleChange('homeAddress', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 resize-none"
              style={{
                borderColor: 'var(--border-light)',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--primary-gold)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-light)';
              }}
            />
          </div>
        </div>
      </div>

      {/* Parent Information Section */}
      <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b-2" style={{ borderColor: 'var(--border-light)' }}>
          <div className="p-2 rounded-lg" style={{ backgroundColor: '#FEF9EC' }}>
            <User className="w-6 h-6" style={{ color: 'var(--primary-gold)' }} />
          </div>
          <h2 className="text-2xl" style={{ color: 'var(--secondary-green)' }}>
            اطلاعات والدین
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="form-group">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              نام پدر *
            </label>
            <input
              type="text"
              required
              value={formData.fatherName}
              onChange={(e) => handleChange('fatherName', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2"
              style={{
                borderColor: 'var(--border-light)',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--primary-gold)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-light)';
              }}
            />
          </div>

          <div className="form-group">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              شماره تماس پدر *
            </label>
            <div className="relative">
              <Phone
                className="absolute left-auto right-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: 'var(--text-gray)' }}
              />
              <input
                type="tel"
                required
                value={formData.fatherPhone}
                onChange={(e) => {
                  // Only allow digits, max 11 digits
                  const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                  handleChange('fatherPhone', value);
                }}
                maxLength={11}
                placeholder="۰۹XX XXX XXXX"
                className="w-full pr-11 pl-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--primary-gold)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-light)';
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              شماره تماس مادر *
            </label>
            <div className="relative">
              <Phone
                className="absolute left-auto right-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: 'var(--text-gray)' }}
              />
              <input
                type="tel"
                required
                value={formData.motherPhone}
                onChange={(e) => {
                  // Only allow digits, max 11 digits
                  const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                  handleChange('motherPhone', value);
                }}
                maxLength={11}
                placeholder="۰۹XX XXX XXXX"
                className="w-full pr-11 pl-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--primary-gold)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-light)';
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              شغل پدر *
            </label>
            <div className="relative">
              <Briefcase
                className="absolute left-auto right-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: 'var(--text-gray)' }}
              />
              <input
                type="text"
                required
                value={formData.fatherJob}
                onChange={(e) => handleChange('fatherJob', e.target.value)}
                className="w-full pr-11 pl-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--primary-gold)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-light)';
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              تحصیلات پدر *
            </label>
            <div className="relative">
              <GraduationCap
                className="absolute left-auto right-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: 'var(--text-gray)' }}
              />
              <select
                required
                value={formData.fatherEducation}
                onChange={(e) => handleChange('fatherEducation', e.target.value)}
                className="w-full pr-11 pl-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--primary-gold)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-light)';
                }}
              >
                <option value="">انتخاب مقطع تحصیلی</option>
                <option value="high-school">زیر دیپلم</option>
                <option value="diploma">دیپلم</option>
                <option value="associate">کاردانی</option>
                <option value="bachelor">کارشناسی</option>
                <option value="master">کارشناسی ارشد</option>
                <option value="doctorate">دکترا</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="block text-sm mb-2" style={{ color: 'var(--text-gray)' }}>
              تحصیلات مادر *
            </label>
            <div className="relative">
              <GraduationCap
                className="absolute left-auto right-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: 'var(--text-gray)' }}
              />
              <select
                required
                value={formData.motherEducation}
                onChange={(e) => handleChange('motherEducation', e.target.value)}
                className="w-full pr-11 pl-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--primary-gold)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border-light)';
                }}
              >
                <option value="">انتخاب مقطع تحصیلی</option>
                <option value="high-school">زیر دیپلم</option>
                <option value="diploma">دیپلم</option>
                <option value="associate">کاردانی</option>
                <option value="bachelor">کارشناسی</option>
                <option value="master">کارشناسی ارشد</option>
                <option value="doctorate">دکترا</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Continue Button */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
      <div


        >
      
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-8 py-4 rounded-xl text-lg transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: 'var(--primary-gold)',
            color: 'var(--secondary-green)',
          }}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></span>
              در حال ذخیره...
            </span>
          ) : (
            '← ادامه به انتخاب زمان مصاحبه'
          )}
        </button>
      </div>
    </form>
  );
}
